import { useCallback, useEffect, useRef, useState } from "react";

import { listOpenHotHoldingAlertsQueryControllerList } from "@/generated/hot-holding-alerts/hot-holding-alerts";
import type { OpenHotHoldingAlertResponseDto } from "@/generated/model";
import { offlineDb, type OfflineOpenHotHoldingAlert } from "@/lib/offline/db";
import { syncService } from "@/lib/offline/syncService";

interface UseOfflineOpenHotHoldingAlertsResult {
  items: OpenHotHoldingAlertResponseDto[];
  isLoading: boolean;
  isOffline: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Offline-first hook for the manager dashboard's "open food alerts" rail
 * (NAU-220). The food sibling of `useOfflineOpenIncidents`:
 *
 *   1. Immediately return cached rows from `offlineDb.openHotHoldingAlerts`.
 *   2. If online, fetch `/v1/nurseries/:id/hot-holding-alerts/open` and replace
 *      the cache.
 *   3. Filter out any alert with a *pending* local acknowledgement so it
 *      doesn't flicker back while a queued sync round-trip is in-flight.
 *   4. Refresh once a queued acknowledgement lands on the server.
 */
export function useOfflineOpenHotHoldingAlerts(
  nurseryId: string | null,
): UseOfflineOpenHotHoldingAlertsResult {
  const [items, setItems] = useState<OpenHotHoldingAlertResponseDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const initializedRef = useRef(false);
  const currentNurseryIdRef = useRef(nurseryId);

  const filterOutPendingAcks = useCallback(
    async (
      raw: OpenHotHoldingAlertResponseDto[],
    ): Promise<OpenHotHoldingAlertResponseDto[]> => {
      if (raw.length === 0) return raw;
      const ids = raw.map((a) => a.id);
      const pending = await offlineDb.hotHoldingAlertAcks
        .filter((a) => ids.includes(a.id) && a.syncState !== "synced")
        .toArray();
      if (pending.length === 0) return raw;
      const pendingIds = new Set(pending.map((p) => p.id));
      return raw.filter((a) => !pendingIds.has(a.id));
    },
    [],
  );

  const loadFromCache = useCallback(async (): Promise<
    OpenHotHoldingAlertResponseDto[]
  > => {
    if (!nurseryId) return [];
    const cached = await offlineDb.openHotHoldingAlerts
      .where("nurseryId")
      .equals(nurseryId)
      .toArray();
    // Newest first, matches the server ordering.
    return cached
      .sort(
        (a, b) =>
          new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime(),
      )
      .map(cacheRowToDto);
  }, [nurseryId]);

  const saveToCache = useCallback(
    async (data: OpenHotHoldingAlertResponseDto[]) => {
      if (!nurseryId) return;
      const fresh = new Set(data.map((a) => a.id));
      const stale = await offlineDb.openHotHoldingAlerts
        .where("nurseryId")
        .equals(nurseryId)
        .toArray();
      const toDelete = stale.filter((s) => !fresh.has(s.id)).map((s) => s.id);
      if (toDelete.length > 0) {
        await offlineDb.openHotHoldingAlerts.bulkDelete(toDelete);
      }
      const cachedAt = Date.now();
      await offlineDb.openHotHoldingAlerts.bulkPut(
        data.map<OfflineOpenHotHoldingAlert>((a) => ({
          id: a.id,
          nurseryId,
          logEntryId: a.logEntryId,
          mealId: a.mealId,
          mealName: a.mealName,
          temperature: a.temperature,
          thresholdAtLog: a.thresholdAtLog,
          recordedAt: a.recordedAt,
          notes: a.notes ?? null,
          cachedAt,
        })),
      );
    },
    [nurseryId],
  );

  const fetchFromServer = useCallback(async (): Promise<
    OpenHotHoldingAlertResponseDto[]
  > => {
    if (!nurseryId) return [];
    const response =
      await listOpenHotHoldingAlertsQueryControllerList(nurseryId);
    return (response?.data as OpenHotHoldingAlertResponseDto[]) || [];
  }, [nurseryId]);

  const refetch = useCallback(async () => {
    if (!nurseryId || !navigator.onLine) return;
    try {
      const fresh = await fetchFromServer();
      await saveToCache(fresh);
      const filtered = await filterOutPendingAcks(fresh);
      setItems(filtered);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch"));
    }
  }, [nurseryId, fetchFromServer, saveToCache, filterOutPendingAcks]);

  // Initial load
  useEffect(() => {
    if (currentNurseryIdRef.current !== nurseryId) {
      currentNurseryIdRef.current = nurseryId;
      initializedRef.current = false;
    }
    if (initializedRef.current) return;
    let mounted = true;

    async function initialize() {
      if (!nurseryId) {
        setItems([]);
        setIsLoading(false);
        return;
      }
      const offline = !navigator.onLine;
      setIsOffline(offline);

      const cached = await loadFromCache();
      if (!mounted) return;
      const filteredCached = await filterOutPendingAcks(cached);
      if (!mounted) return;
      setItems(filteredCached);
      setIsLoading(false);

      if (!offline) {
        try {
          const fresh = await fetchFromServer();
          if (!mounted) return;
          await saveToCache(fresh);
          const filtered = await filterOutPendingAcks(fresh);
          if (!mounted) return;
          setItems(filtered);
          setError(null);
        } catch (err) {
          if (mounted && cached.length === 0) {
            setError(err instanceof Error ? err : new Error("Failed to fetch"));
          }
        }
      }

      if (mounted) initializedRef.current = true;
    }

    initialize();
    return () => {
      mounted = false;
    };
  }, [
    nurseryId,
    loadFromCache,
    fetchFromServer,
    saveToCache,
    filterOutPendingAcks,
  ]);

  // Online/offline listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      refetch();
    };
    const handleOffline = () => setIsOffline(true);
    setIsOffline(!navigator.onLine);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [refetch]);

  // Sync subscription — refetch when a queued acknowledgement lands
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  const previousPendingCountRef = useRef(0);
  useEffect(() => {
    const unsubscribe = syncService.subscribe((status) => {
      const prev = previousPendingCountRef.current;
      if (prev > 0 && status.pendingCount === 0) {
        refetchRef.current();
      }
      previousPendingCountRef.current = status.pendingCount;
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return { items, isLoading, isOffline, error, refetch };
}

function cacheRowToDto(
  row: OfflineOpenHotHoldingAlert,
): OpenHotHoldingAlertResponseDto {
  return {
    id: row.id,
    logEntryId: row.logEntryId,
    mealId: row.mealId,
    mealName: row.mealName,
    nurseryId: row.nurseryId,
    temperature: row.temperature,
    thresholdAtLog: row.thresholdAtLog,
    recordedAt: row.recordedAt,
    notes: row.notes,
  };
}
